import React from 'react';

// The way into "Search night trains nearby" (docs/product/night-trains/README.md),
// wherever it is offered: the leg sheet, the leg cards, the journey's summary.
// One component, so the action looks and reads the same everywhere. It only
// asks: the page owns the dialog (NightTrainsNearbyDialog) and opens it, so
// the search is never implemented twice. The foot of the transport lists
// calls the same handler through the shared Dropdown's `footer`.
//
// Where the search would find nothing (`blocked`, from the journey payload's
// `nightTrainSearch`; Paulo 2026-09-29) the action is not offered: in its
// place, quietly, the reason ("No night trains found nearby"). Said in words
// and not in a tooltip, so it reads on a phone too.

type Variant =
  /** A button of its own (leg sheet). */
  | 'button'
  /** The same button on the journey summary's indigo pill. */
  | 'button-on-night'
  /** A quiet text link inside a line of stats (leg card). */
  | 'link'
  /** The same link on the dark night-train card. */
  | 'link-night';

interface NightTrainSearchButtonProps {
  label: string;
  variant?: Variant;
  disabled?: boolean;
  /** The search would find nothing: shown in place of the label, and nothing opens
   *  (nightTrain.ts, searchBlockedReason). */
  blocked?: string | null;
  /** For screen readers, when the label alone does not say which part of the trip. */
  ariaLabel?: string;
  onClick: () => void;
}

const STYLES: Record<Variant, React.CSSProperties> = {
  button: {
    display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none', whiteSpace: 'nowrap',
    font: 'inherit', fontSize: 11.5, fontWeight: 700, borderRadius: 12, padding: '8px 12px',
    background: '#ecebf7', color: '#34335c', border: 'none',
  },
  'button-on-night': {
    display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none', whiteSpace: 'nowrap',
    font: 'inherit', fontSize: 11.5, fontWeight: 700, borderRadius: 12, padding: '8px 12px',
    background: '#fff', color: '#34335c', border: 'none',
  },
  link: {
    display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
    font: 'inherit', fontWeight: 700, background: 'none', border: 'none', padding: 0, color: '#4a4b8c',
  },
  'link-night': {
    display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
    font: 'inherit', fontWeight: 700, background: 'none', border: 'none', padding: 0, color: '#ececff',
  },
};

// The reason, in place of the action: the same shape, without its colour.
const BLOCKED: Record<Variant, React.CSSProperties> = {
  button: { background: '#f4f6f3', color: '#8a948f', fontWeight: 600, whiteSpace: 'normal', textAlign: 'left', flex: '0 1 auto' },
  'button-on-night': { background: 'rgba(255,255,255,0.55)', color: '#6d6c8f', fontWeight: 600, whiteSpace: 'normal', textAlign: 'left', flex: '0 1 auto' },
  link: { color: '#8a948f', fontWeight: 500 },
  'link-night': { color: '#b7b9e2', fontWeight: 500 },
};

const NightTrainSearchButton: React.FC<NightTrainSearchButtonProps> = ({ label, variant = 'button', disabled, blocked, ariaLabel, onClick }) => {
  if (blocked) {
    return (
      <span
        // Inside a card that opens a sheet on click: reading it opens nothing.
        onClick={(e) => e.stopPropagation()}
        style={{ ...STYLES[variant], ...BLOCKED[variant], cursor: 'default' }}
      >
        <span aria-hidden style={{ filter: 'grayscale(1)', opacity: 0.55 }}>🌙</span>
        <span>{blocked}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      // Often inside a card that opens a sheet on click: only this action runs.
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ ...STYLES[variant], cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}
    >
      <span aria-hidden>🌙</span>
      <span style={variant === 'button' || variant === 'button-on-night' ? undefined : { textDecoration: 'underline', textDecorationColor: 'currentColor', textUnderlineOffset: 2, textDecorationThickness: 1 }}>{label}</span>
    </button>
  );
};

export default NightTrainSearchButton;
